export type TransportMapVariableId =
  | 'ev_adoption'
  | 'total_evs'
  | 'total_chargers'
  | 'vehicles_per_household'
  | 'zero_vehicle_households'
  | 'commute_time'
  | 'walkability'
  | 'population';

export interface TransportMapMethodologyBundle {
  overviewTitle: string;
  metricLabel: string;
  primarySources: string[];
  calculation: string[];
  limitations: string[];
  technicalDetails: string[];
}

const BUNDLES: Record<string, TransportMapMethodologyBundle> = {
  ev_adoption: {
    overviewTitle: 'Electric vehicle adoption (map)',
    metricLabel: 'Plug-in electric vehicles per 100 people',
    primarySources: [
      'California Air Resources Board EMFAC Fleet Database — registration-derived fleet counts; native geography: census block group.',
      'U.S. Census Bureau population — tract and place denominators.',
    ],
    calculation: [
      'Block-group fleet counts are aggregated to census tracts using ClimatePlans geographic correspondence.',
      'Tracts are assigned to the selected city or county for map display and scope comparisons.',
      'California reference uses the same per-100-people definition across tracts in the loaded scope.',
    ],
    limitations: [
      'Tract rates can be unstable where population is very small.',
      'Fleet registration geography may not match where vehicles are primarily used.',
    ],
    technicalDetails: ['ev_by_tract_2023', 'relationship_trct_place_20', 'GenericMapTiered.tsx'],
  },
  total_evs: {
    overviewTitle: 'Total electric vehicles (map)',
    metricLabel: 'Estimated plug-in vehicles per tract',
    primarySources: [
      'CARB EMFAC Fleet / DMV registration-derived data at block group.',
      'U.S. Census Bureau tract population (2023 vintage in map layer).',
    ],
    calculation: [
      'When ev_by_tract_2023 includes native plug-in fleet count columns, those values are summed/displayed directly.',
      'Otherwise tract EV count = (EVs per 100 people ÷ 100) × tract population (rate field rolled up from block-group fleet registrations).',
      'Scope peer averages use unweighted mean across tracts in the selected comparison geography.',
    ],
    limitations: ['High counts in dense tracts do not imply high per-capita adoption.'],
    technicalDetails: ['ev_by_tract_2023', 'mapTractStatistics.ts'],
  },
  total_chargers: {
    overviewTitle: 'EV charging (map)',
    metricLabel: 'Public EVSE ports per tract',
    primarySources: [
      'U.S. Department of Energy Alternative Fuels Data Center (AFDC) Station Locator — public EVSE port counts with documented access/status filters; snapshot date applies.',
      'Geographic assignment to census tracts via ClimatePlans spatial join.',
    ],
    calculation: [
      'Public EVSE ports are summed per census tract (not station locations as a separate public metric on this layer).',
      'California reference is tract-level mean within the loaded map extent when peer data are available.',
    ],
    limitations: [
      'Private residential charging is not fully represented in AFDC public station data.',
      'Station status (available vs planned) affects counts.',
    ],
    technicalDetails: ['ev_chargers / AFDC ingest scripts', 'relationship_trct_place_20'],
  },
  vehicles_per_household: {
    overviewTitle: 'Vehicles per household (map)',
    metricLabel: 'ACS vehicles available per household',
    primarySources: [
      'U.S. Census Bureau American Community Survey 5-year estimates — household vehicle availability (see transportation_acs_crosswalk.csv for verified table/variables).',
    ],
    calculation: [
      'Tract-level ACS estimates are mapped to places using census relationship files.',
    ],
    limitations: ['ACS is survey-based with margin of error at tract scale.'],
    technicalDetails: ['ev_by_tract_2023 vehicles-per-household field', 'relationship_trct_place_20', 'python/census_query.py'],
  },
  zero_vehicle_households: {
    overviewTitle: 'Zero-vehicle households (map)',
    metricLabel: 'Share of households with no vehicle available (%)',
    primarySources: [
      'U.S. Census Bureau American Community Survey 5-year estimates — Table B08201 (vehicles available by household size); pct_zero_vehicle_households derived from B08201_002E ÷ B08201_001E (see python/census_query.py).',
    ],
    calculation: [
      'Display tract share of households with no vehicle available from the ACS-derived percentage field.',
      'Do not estimate household counts from population ÷ average household size.',
    ],
    limitations: [
      'Survey-based tract estimate; describes vehicle availability, not trip behavior or mobility adequacy.',
    ],
    technicalDetails: ['pct_zero_vehicle_households in ev_by_tract_2023', 'relationship_trct_place_20'],
  },
  commute_time: {
    overviewTitle: 'Commute time (map)',
    metricLabel: 'Mean travel time to work (minutes)',
    primarySources: [
      'U.S. Census Bureau ACS 5-year — travel time to work for workers 16+.',
    ],
    calculation: ['Tract-level mean commute time joined to place geographies.'],
    limitations: ['Reflects pre-pandemic ACS window depending on vintage; not total daily travel.'],
    technicalDetails: ['ACS commute time tract layer'],
  },
  walkability: {
    overviewTitle: 'Walkability (map)',
    metricLabel: 'EPA National Walkability Index (population-weighted tract score)',
    primarySources: [
      'U.S. EPA National Walkability Index (June 2021 release) — native geography: census block group.',
      'U.S. Census Bureau population for weighting.',
    ],
    calculation: [
      'Block-group index scores are population-weighted to census tracts.',
      'California percentile classification ranks tracts relative to statewide distribution.',
    ],
    limitations: ['Index summarizes built environment; it does not measure perceived safety or sidewalk maintenance.'],
    technicalDetails: ['walkability_tract', 'build_walkability_tract_from_epa_gdb.py', 'california_tracts_optimized.geojson'],
  },
  population: {
    overviewTitle: 'Population (map)',
    metricLabel: 'Census tract population',
    primarySources: ['U.S. Census Bureau population estimates (vintage used in map layer).'],
    calculation: ['Tract population displayed with place/county scope aggregation for KPI peers.'],
    limitations: ['Point-in-time estimate; not housing unit counts.'],
    technicalDetails: ['population tract layer', 'relationship_trct_place_20'],
  },
};

export function transportMapMethodology(variable: string): TransportMapMethodologyBundle {
  return BUNDLES[variable] ?? {
    overviewTitle: 'Transportation map layer',
    metricLabel: 'Selected map indicator',
    primarySources: ['See Technical Details for the active Supabase layer.'],
    calculation: ['Tract values are joined to the selected place using ClimatePlans correspondence tables.'],
    limitations: ['Interpret tract patterns with population and margin of error in mind.'],
    technicalDetails: [variable],
  };
}
